import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import ClientShell from "@/components/ClientShell";
import Home from "@/pages/client/Home";
import Catalog from "@/pages/client/Catalog";
import ProductDetail from "@/pages/client/ProductDetail";
import CartPage from "@/pages/client/CartPage";
import CheckoutPage from "@/pages/client/CheckoutPage";
import OrdersPage from "@/pages/client/OrdersPage";
import OrderDetailPage from "@/pages/client/OrderDetailPage";
import QuotesPage from "@/pages/client/QuotesPage";
import InstallationsPage from "@/pages/client/InstallationsPage";
import ProfilePage from "@/pages/client/ProfilePage";
import { About, Terms, Privacy } from "@/pages/client/StaticPages";
import DynamicPage from "@/pages/client/DynamicPage";
import InfosPage from "@/pages/client/InfosPage";
import AdminPages from "@/pages/admin/AdminPages";
import ClientSupport from "@/pages/client/ClientSupport";
import ProjectPage from "@/pages/client/ProjectPage";
import AdminProjects from "@/pages/admin/AdminProjects";
import MessagingPage from "@/pages/MessagingPage";
import AdminMessaging from "@/pages/admin/AdminMessaging";
import AdminActivityLog from "@/pages/admin/AdminActivityLog";
import AdminFlashSales from "@/pages/admin/AdminFlashSales";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import CompleteProfile from "@/pages/auth/CompleteProfile";
import AuthCallback from "@/pages/auth/AuthCallback";

import ProShell from "@/pages/pro/ProShell";
import ProDashboard from "@/pages/pro/ProDashboard";
import ProMissionDetail from "@/pages/pro/ProMissionDetail";
import ProEarnings from "@/pages/pro/ProEarnings";
import LivreurApp from "@/pages/livreur/LivreurApp";
import Bordereau from "@/pages/Bordereau";

import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminQuotes from "@/pages/admin/AdminQuotes";
import AdminInstallations from "@/pages/admin/AdminInstallations";
import AdminInstallPlanning from "@/pages/admin/AdminInstallPlanning";
import AdminDeliveries from "@/pages/admin/AdminDeliveries";
import AdminProfessionals from "@/pages/admin/AdminProfessionals";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminTreasury from "@/pages/admin/AdminTreasury";
import AdminClients from "@/pages/admin/AdminClients";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminZones from "@/pages/admin/AdminZones";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminInfluencers from "@/pages/admin/AdminInfluencers";
import InfluencerDashboard from "@/pages/influencer/InfluencerDashboard";
import DevenirInfluenceur from "@/pages/client/DevenirInfluenceur";
import BlogList from "@/pages/client/BlogList";
import BlogDetail from "@/pages/client/BlogDetail";
import AdminBlog from "@/pages/admin/AdminBlog";
import RefCapture from "@/pages/RefCapture";
import RefCaptureClient from "@/pages/RefCaptureClient";

const STAFF = ["admin", "super_admin", "responsable_logistique", "responsable_installation", "commercial", "comptable"];

function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth callback (session_id in URL fragment) before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/r/:code" element={<RefCapture />} />
      <Route path="/rc/:code" element={<RefCaptureClient />} />
      <Route path="/completer-profil" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />

      {/* Client storefront */}
      <Route element={<ClientShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/recherche" element={<Catalog />} />
        <Route path="/produit/:id" element={<ProductDetail />} />
        <Route path="/panier" element={<CartPage />} />
        <Route path="/commander" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/commandes" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
        <Route path="/commandes/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
        <Route path="/devis" element={<ProtectedRoute><QuotesPage /></ProtectedRoute>} />
        <Route path="/mes-installations" element={<ProtectedRoute><InstallationsPage /></ProtectedRoute>} />
        <Route path="/projet" element={<ProtectedRoute><ProjectPage /></ProtectedRoute>} />
        <Route path="/compte" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/a-propos" element={<About />} />
        <Route path="/cgu" element={<Terms />} />
        <Route path="/confidentialite" element={<Privacy />} />
        <Route path="/support" element={<ClientSupport />} />
        <Route path="/pages/:slug" element={<DynamicPage />} />
        <Route path="/infos" element={<InfosPage />} />
        <Route path="/devenir-influenceur" element={<DevenirInfluenceur />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
      </Route>

      {/* Influenceur */}
      <Route path="/influenceur" element={<ProtectedRoute roles={["influencer"]}><InfluencerDashboard /></ProtectedRoute>} />

      {/* Professionnel */}
      <Route path="/pro" element={<ProtectedRoute roles={["professional"]}><ProShell /></ProtectedRoute>}>
        <Route index element={<ProDashboard />} />
        <Route path="mission/:id" element={<ProMissionDetail />} />
        <Route path="gains" element={<ProEarnings />} />
      </Route>

      {/* Livreur */}
      <Route path="/livreur" element={<ProtectedRoute roles={["livreur"]}><LivreurApp /></ProtectedRoute>} />

      {/* Messagerie interne — tous rôles authentifiés */}
      <Route path="/messages" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />

      {/* Bordereau de livraison (imprimable) */}
      <Route path="/bordereau/:id" element={<ProtectedRoute><Bordereau /></ProtectedRoute>} />

      {/* Admin back-office */}
      <Route path="/admin" element={<ProtectedRoute roles={STAFF}><AdminShell /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="catalogue" element={<AdminCatalog />} />
        <Route path="pages" element={<AdminPages />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="promotions" element={<AdminPromotions />} />
        <Route path="ventes-flash" element={<AdminFlashSales />} />
        <Route path="commandes" element={<AdminOrders />} />
        <Route path="devis" element={<AdminQuotes />} />
        <Route path="projets" element={<AdminProjects />} />
        <Route path="livraisons" element={<AdminDeliveries />} />
        <Route path="installations" element={<AdminInstallations />} />
        <Route path="planning" element={<AdminInstallPlanning />} />
        <Route path="professionnels" element={<AdminProfessionals />} />
        <Route path="paiements" element={<AdminPayments />} />
        <Route path="tresorerie" element={<AdminTreasury />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="avis" element={<AdminReviews />} />
        <Route path="zones" element={<AdminZones />} />
        <Route path="personnel" element={<AdminStaff />} />
        <Route path="utilisateurs" element={<AdminStaff />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="messagerie" element={<AdminMessaging />} />
        <Route path="journal" element={<AdminActivityLog />} />
        <Route path="influenceurs" element={<AdminInfluencers />} />
        <Route path="parametres" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
